import {
	Box,
	Dialog,
	DialogContent,
	DialogTitle,
	FormControl,
	FormHelperText,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography
} from "@mui/material";
import React, { FC, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, FieldValues, useForm } from "react-hook-form";
import { NUMBER_PLATE_REGEX, VEHICLE_TYPES_LIST } from "../../../../utils/constants";
import { LoadingButton } from "@mui/lab";
import { useAddBlacklistVehicleMutation } from "../../../../redux/reducers/dataManagement.reducers";

interface IAddBlacklistVehicleDialogProps {
	open: boolean;
	onClose: () => void;
}

const AddBlacklistVehicleDialog: FC<IAddBlacklistVehicleDialogProps> = (props) => {
	const { open, onClose } = props;

	const [
		addBlacklistVehicle,
		{
			isLoading: addVehicleLoading,
			isSuccess: addVehicleSuccess,
			error: addVehicleError,
			reset: resetAddBlacklistVehicleState
		}
	] = useAddBlacklistVehicleMutation();

	const {
		register,
		control,
		handleSubmit,
		reset,
		formState: { errors }
	} = useForm();

	function handleSubmitAddVehicleForm(data: FieldValues) {
		addBlacklistVehicle({
			owner: null,
			number_plate: String(data.vehicle_number).toUpperCase(),
			vehicle_type: Number(data.vehicle_type),
			comment: data.reason,
			auth_type: "blacklist"
		});
	}

	useEffect(() => {
		reset({
			vehicle_number: "",
			vehicle_type: "",
			reason: ""
		});
	}, [open, reset]);

	useEffect(() => {
		if (addVehicleSuccess) {
			onClose();
			resetAddBlacklistVehicleState();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [addVehicleSuccess]);

	return (
		<Dialog 
			open={open} 
			onClose={onClose} 
			fullWidth 
			maxWidth="md"
			sx={{
				"& .MuiDialog-paper": {
					margin: { xs: "1rem", sm: "2rem" },
					width: { xs: "calc(100% - 2rem)", sm: "auto" },
					maxWidth: { xs: "100%", sm: "900px" },
					maxHeight: { xs: "calc(100vh - 2rem)", sm: "90vh" }
				}
			}}
		>
			<DialogTitle
				textAlign="center"
				sx={{
					background: "linear-gradient(135deg, #d32f2f 0%, #f44336 100%)",
					color: "white",
					position: "relative",
					padding: { xs: "0.75rem 1rem 0.5rem 1rem", sm: "16px 20px 12px 20px" },
					borderBottom: "1px solid rgba(255, 255, 255, 0.1)"
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
					<CloseIcon sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }} />
					<Typography variant="h6" sx={{ 
						fontWeight: 600,
						fontSize: { xs: "1.125rem", sm: "1.25rem" }
					}}>
						Add Blacklist Vehicle
					</Typography>
				</Box>
				<Box sx={{ position: "absolute", top: { xs: 8, sm: 12 }, right: { xs: 8, sm: 12 } }}>
					<IconButton
						onClick={onClose}
						sx={{
							color: "white",
							background: "rgba(255, 255, 255, 0.1)",
							backdropFilter: "blur(8px)",
							borderRadius: "50%",
							padding: { xs: "4px", sm: "6px" },
							"&:hover": {
								background: "rgba(255, 255, 255, 0.2)",
								transform: "scale(1.1)"
							},
							transition: "all 0.2s ease-in-out"
						}}
					>
						<CloseIcon sx={{ fontSize: { xs: "20px", sm: "24px" } }} />
					</IconButton>
				</Box>
			</DialogTitle>

			<DialogContent
				sx={{
					padding: { xs: "1rem !important", sm: "20px !important" },
					background: "linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)",
					overflowY: "auto"
				}}
			>
				<Grid container spacing={{ xs: 2, sm: 2 }} component="form" noValidate onSubmit={handleSubmit(handleSubmitAddVehicleForm)}>
					<Grid item xs={12} md={6}>
						<InputLabel
							htmlFor="vehicle-number-input"
							required
							error={!!errors.vehicle_number}
							sx={{
								fontWeight: 600,
								color: "#3D3D3D",
								marginBottom: "0.5rem",
								fontSize: { xs: "0.8125rem", sm: "0.875rem" }
							}}
						>
							Vehicle Number
						</InputLabel>

						<TextField
							required
							fullWidth
							variant="outlined"
							color="primary"
							size="small"
							id="vehicle-number-input"
							placeholder="Enter the vehicle number"
							autoComplete="off"
							error={!!errors.vehicle_number}
							helperText={
								errors.vehicle_number && errors.vehicle_number.message ? errors.vehicle_number.message.toString() : ""
							}
							classes={{ root: "vehicle-number-input-root" }}
							sx={{
								"& .MuiOutlinedInput-root": {
									borderRadius: "8px",
									background: "rgba(255, 255, 255, 0.8)",
									"&:hover": {
										background: "rgba(255, 255, 255, 0.9)"
									},
									"&.Mui-focused": {
										background: "rgba(255, 255, 255, 0.95)"
									}
								}
							}}
							{...register("vehicle_number", {
								required: "Vehicle Number is required",
								pattern: { value: NUMBER_PLATE_REGEX, message: "Please enter a valid Vehicle Number" }
							})}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<InputLabel
							htmlFor="vehicle-type-input"
							required
							error={!!errors.vehicle_type}
							sx={{
								fontWeight: 600,
								color: "#3D3D3D",
								marginBottom: "0.5rem",
								fontSize: { xs: "0.8125rem", sm: "0.875rem" }
							}}
						>
							Vehicle Type
						</InputLabel>

						<Controller
							name="vehicle_type"
							control={control}
							render={({ field: { value, onChange } }) => (
								<FormControl variant="outlined" fullWidth required error={!!errors.vehicle_type} size="small">
									<Select
										required
										fullWidth
										id="vehicle-type-input"
										value={value}
										onChange={onChange}
										sx={{
											borderRadius: "8px",
											background: "rgba(255, 255, 255, 0.8)",
											"&:hover": {
												background: "rgba(255, 255, 255, 0.9)"
											},
											"&.Mui-focused": {
												background: "rgba(255, 255, 255, 0.95)"
											}
										}}
									>
										{VEHICLE_TYPES_LIST.map((vehicleTypeItem) => (
											<MenuItem value={vehicleTypeItem.id} key={vehicleTypeItem.id}>
												{vehicleTypeItem.name}
											</MenuItem>
										))}
									</Select>

									{errors.vehicle_type && errors.vehicle_type.message ? (
										<FormHelperText error>{errors.vehicle_type.message.toString()}</FormHelperText>
									) : null}
								</FormControl>
							)}
						/>
					</Grid>

					<Grid item xs={12}>
						<InputLabel
							htmlFor="reason-input"
							sx={{
								fontWeight: 600,
								color: "#3D3D3D",
								marginBottom: "0.5rem",
								fontSize: { xs: "0.8125rem", sm: "0.875rem" }
							}}
						>
							Reason
						</InputLabel>

						<TextField
							fullWidth
							multiline
							rows={3}
							variant="outlined"
							color="primary"
							size="small"
							id="reason-input"
							placeholder="Enter the reason for blacklisting"
							autoComplete="off"
							error={!!errors.reason}
							helperText={errors.reason && errors.reason.message ? errors.reason.message.toString() : ""}
							sx={{
								"& .MuiOutlinedInput-root": {
									borderRadius: "8px",
									background: "rgba(255, 255, 255, 0.8)",
									"&:hover": {
										background: "rgba(255, 255, 255, 0.9)"
									},
									"&.Mui-focused": {
										background: "rgba(255, 255, 255, 0.95)"
									}
								}
							}}
							{...register("reason")}
						/>
					</Grid>

					{addVehicleError ? (
						<Grid item xs={12}>
							<Box
								sx={{
									padding: { xs: "10px 12px", sm: "12px 16px" },
									background: "linear-gradient(135deg, rgba(211, 47, 47, 0.08) 0%, rgba(211, 47, 47, 0.04) 100%)",
									borderRadius: "8px",
									border: "1px solid rgba(211, 47, 47, 0.1)",
									marginTop: 1
								}}
							>
								<Typography variant="body2" color="#d32f2f" textAlign="center" sx={{ 
									fontWeight: 500,
									fontSize: { xs: "0.8125rem", sm: "0.875rem" }
								}}>
									{"data" in addVehicleError && addVehicleError.data
										? `${String(addVehicleError.data).slice(0, 1).toUpperCase()}${String(addVehicleError.data).slice(
												1
										  )}`
										: "Something went wrong"}
								</Typography>
							</Box>
						</Grid>
					) : null}

					<Grid item xs={12}>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								marginTop: 1
							}}
						>
							<LoadingButton
								variant="contained"
								color="error"
								type="submit"
								loading={addVehicleLoading}
								sx={{
									background: "linear-gradient(135deg, #d32f2f 0%, #f44336 100%)",
									borderRadius: "8px",
									padding: { xs: "8px 24px", sm: "8px 32px" },
									fontWeight: 600,
									minWidth: { xs: "120px", sm: "140px" },
									fontSize: { xs: "0.875rem", sm: "1rem" },
									"&:hover": {
										background: "linear-gradient(135deg, #c62828 0%, #d32f2f 100%)",
										boxShadow: "0px 4px 12px rgba(211, 47, 47, 0.3)",
										transform: "translateY(-1px)"
									},
									transition: "all 0.2s ease-in-out"
								}}
							>
								Save Vehicle
							</LoadingButton>
						</Box>
					</Grid>
				</Grid>
			</DialogContent>
		</Dialog>
	);
};

export default AddBlacklistVehicleDialog;
